"use client";

import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from "react";

type ToastKind = "success" | "error" | "warning" | "info";

interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastApi {
  toast: (message: string, kind?: ToastKind) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  warning: (message: string) => void;
  info: (message: string) => void;
}

const noop = () => {};
const ToastContext = createContext<ToastApi>({ toast: noop, success: noop, error: noop, warning: noop, info: noop });

let nextId = 1;

const ICONS: Record<ToastKind, string> = {
  success: "M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  error: "M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  warning: "M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z",
  info: "M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
};

const CHIP: Record<ToastKind, string> = {
  success: "bg-success-50 text-success-600 dark:bg-success-500/15 dark:text-success-400",
  error: "bg-danger-50 text-danger-600 dark:bg-danger-500/15 dark:text-danger-400",
  warning: "bg-warning-50 text-warning-600 dark:bg-warning-500/15 dark:text-warning-400",
  info: "bg-accent-50 text-accent-600 dark:bg-accent-500/15 dark:text-accent-300"
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const remove = useCallback((id: number) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const toast = useCallback(
    (message: string, kind: ToastKind = "info") => {
      const id = nextId++;
      setToasts((t) => [...t, { id, kind, message }]);
      setTimeout(() => remove(id), 4500);
    },
    [remove]
  );

  // Memoized: the provider re-renders every time a toast is shown, and a fresh object here would
  // hand every consumer a new `toast` identity each time. Anything with `useCallback`/`useEffect`
  // keyed on it would then re-run on every toast — an infinite loop if that work shows a toast.
  const api = useMemo<ToastApi>(
    () => ({
      toast,
      success: (m) => toast(m, "success"),
      error: (m) => toast(m, "error"),
      warning: (m) => toast(m, "warning"),
      info: (m) => toast(m, "info")
    }),
    [toast]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed bottom-5 right-5 z-[60] flex w-[22rem] max-w-[calc(100vw-2.5rem)] flex-col gap-2">
        {toasts.map((t) => (
          <div
            key={t.id}
            role="status"
            className="pointer-events-auto flex animate-slide-in-right items-start gap-3 rounded-xl border border-gray-200/70 bg-white px-4 py-3 text-sm text-gray-800 shadow-dropdown ring-1 ring-black/5"
          >
            <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${CHIP[t.kind]}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="h-4 w-4">
                <path strokeLinecap="round" strokeLinejoin="round" d={ICONS[t.kind]} />
              </svg>
            </span>
            <span className="flex-1 pt-0.5">{t.message}</span>
            <button onClick={() => remove(t.id)} className="text-gray-400 transition hover:text-gray-600" aria-label="Dismiss">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-4 w-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
